import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { Store } from "@ngrx/store";
import { SiteSection } from "@serasa/shared/enums/site-section.enum";
import { AnalyticsService } from "@serasa/shared/services/analytics.service";
import { SedsModalService, SedsSizeOptions } from "experian-design-system"; // NOSONAR
import { Observable, tap } from "rxjs";

import { MAT_DIALOG_DATA } from "@angular/material/dialog";
import { BlockedReasonsENEnum } from "../../../../../shared/enums/blocked-reasons.enum";
import { UnblockStageEnum } from "../../../../../shared/enums/unblock-stage.enum";
import { SidenavManagerService } from "../../../../../shared/services/sidenav.manager.service";
import { Logon } from "../../../users/interfaces/logon.interface";
import { ResetUser } from "../../../users/interfaces/reset-user.interface";
import { User } from "../../../users/interfaces/user.interface";
import {
  loadSelectedLogon,
  loadToggleSidenav,
  resetUser,
} from "../../../users/state/actions/user.actions";
import {
  selectLogon,
  selectResetStatus,
} from "../../../users/state/selectors/user.selectors";
import { UnblockUserFeature } from "../../interfaces/dashboard-state.interface";
import {
  loadResetBlockUser,
  loadUnblockUser,
} from "../../state/actions/dashboard.actions";
import { selectUnblockUserFeature } from "../../state/selectors/dashboard.selector";
import { ResetPasswordComponent } from "../reset-password/reset-password.component";

@Component({
    selector: "pl-unblock-user-component",
    templateUrl: "./unblock-user.component.html",
    styleUrls: ["./unblock-user.component.scss", "../../../../../styles/theme/modal.scss"],
    standalone: false
})
export class UnblockUserComponent implements OnDestroy, OnInit {
  private hasDoubleBlock = false;
  private isBlockedByWrongPass = false;
  private isBlockedManually = false;

  public UnblockStageEnum = UnblockStageEnum;
  public isLoading = false;
  public message = "";
  public resetUserData!: ResetUser;
  public selectUnblockUserFeature$!: Observable<any>;
  public selectedLogon$!: Observable<any>;
  public selectedLogon: any = null;
  public smallButton = SedsSizeOptions.SMALL;
  public stage = UnblockStageEnum.INITIAL_UNBLOCK;
  public isEdition = false;
  public isResetPassword = false;

  constructor(
    private readonly _dialog: SedsModalService,
    private readonly _sidenavManagerService: SidenavManagerService,
    private readonly _analyticsService: AnalyticsService,
    private readonly store: Store,
    @Inject(MAT_DIALOG_DATA) public data: { byUserDetails: boolean },
  ) {}

  ngOnInit() {
    this.selectedLogon$ = this.store.select(selectLogon);
    this.selectUnblockUserFeature$ = this.store.select(
      selectUnblockUserFeature,
    );
    this.selectedLogon$.subscribe((selectedLogon) => {
      this.selectedLogon = selectedLogon;
      this.checkStage(selectedLogon);
    });

    this._analyticsService.doPageLoad(SiteSection.DESBLOQUEAR_USUARIO);
    this.checkInitialState();
  }

  public checkInitialState() {
    this.setBlockTypes();

    this.selectUnblockUserFeature$.subscribe(
      (blockResponse: UnblockUserFeature) => {
        const { response, error, isLoading } = blockResponse;
        this.isLoading = isLoading;
        this.message = "";

        if (error) {
          return this.setStages(UnblockStageEnum.ERROR);
        } else if (response) {
          if (this.hasDoubleBlock) {
            return this.resetPassword();
          } else {
            this.checkBlockTypes();
          }
        }
      },
    );
  }

  public checkBlockTypes() {
    if (this.isBlockedManually && this.isBlockedByWrongPass) {
      this.setStages(UnblockStageEnum.SUCCESS_EMAIL_SENT);
    }

    if (this.isBlockedByWrongPass) {
      this.setStages(UnblockStageEnum.SUCCESS_EMAIL_SENT);
    }

    if (this.isBlockedManually || this.isBlockedByWrongPass) {
      this.setStages(UnblockStageEnum.RESET_FLOW_ACTIVE);
    }
  }

  public unblockUser(): void {
    this.dispatchUnblock();
  }

  public dispatchUnblock() {
    this.setBlockTypes();

    if (this.isBlockedByWrongPass && this.isBlockedManually) {
      this.hasDoubleBlock = true;
      this.unblockScreen();
    } else if (this.isBlockedByWrongPass) {
      this.resetPassword();
    } else if (this.isBlockedManually) {
      this.unblockScreen();
    }
  }

  public unblockScreen() {
    this.store.dispatch(
      loadUnblockUser({
        payload: {
          logon: this.selectedLogon?.logon,
        },
      }),
    );
  }

  public resetPassword() {
    this.store.dispatch(resetUser({ resetUser: this.resetUserData }));
    this.store
      .select(selectResetStatus)
      .pipe(
        tap((reset) => {
          if (reset === "success") {
            this.message = `Uma senha temporária foi enviada ao e-mail do usuário`;
            this.setStages(UnblockStageEnum.SUCCESS_EMAIL_SENT);
          } else if (reset === "error") {
            this.setStages(UnblockStageEnum.ERROR);
          }
        }),
      )
      .subscribe();
  }

  public checkPasswordBlock(blockType: string): boolean {
    return this.selectedLogon?.blockageTypes?.some(
      (item: any) => item.description === blockType,
    );
  }

  public openResetPassword(): void {
    this.isResetPassword = true;
    this._dialog.open(ResetPasswordComponent, null, {width: '513px'});
  }

  public editUser(): void {
    this.isEdition = true;
    const data = {
      ...this.selectedLogon,
      name: this.selectedLogon.fullname,
    };

    this._setCurrentUser(data);
    this._dialog.close();
    this._openSidenav();

    this._sidenavManagerService.navigate$.next("edit");
  }

  checkStage(selectedLogon: Logon) {
    const { fullname, document, alternativeEmail, phone, logon } =
      selectedLogon || {};
    const canEdit = Boolean(
      fullname && document && (alternativeEmail || phone),
    );

    this.setBlockTypes();

    if (canEdit) {
      this.resetUserData = this.getResetUserData(
        fullname,
        alternativeEmail?.email,
        logon,
      );
    }

    this.setStages(this.calculateUnblockStage(canEdit));
  }

  getResetUserData(
    fullname: string | undefined,
    email: string | undefined,
    logon: string | undefined,
  ) {
    return {
      fullName: fullname || "",
      email: email || "",
      logon: logon || "",
    };
  }

  public calculateUnblockStage(canEdit: boolean) {
    const userCanEdit = canEdit
      ? UnblockStageEnum.PASS_UNBLOCK
      : UnblockStageEnum.NEED_EDIT;

    if (this.isBlockedByWrongPass && this.isBlockedManually) {
      return userCanEdit;
    } else if (this.isBlockedManually && !this.isBlockedByWrongPass) {
      return UnblockStageEnum.INITIAL_UNBLOCK;
    } else if (!this.isBlockedManually && this.isBlockedByWrongPass) {
      return userCanEdit;
    } else {
      return UnblockStageEnum.INITIAL_UNBLOCK;
    }
  }

  private setBlockTypes() {
    this.isBlockedByWrongPass = this.checkPasswordBlock(
      BlockedReasonsENEnum.BLOCKED_BY_WRONG_PASS,
    );
    this.isBlockedManually = this.checkPasswordBlock(
      BlockedReasonsENEnum.BLOCKED_MANUALLY,
    );
  }

  private setStages(stage: UnblockStageEnum) {
    this.stage = stage;
  }

  private _setCurrentUser(user: User) {
    this.store.dispatch(
      loadSelectedLogon({
        selectedUserId: user?.userId || "",
        userDataTable: user,
      }),
    );
  }

  private _openSidenav() {
    this.store.dispatch(
      loadToggleSidenav({
        openSidenav: true,
      }),
    );
  }

  ngOnDestroy(): void {
    if (!this.data?.byUserDetails && !this.isEdition && !this.isResetPassword) {
      this._sidenavManagerService.navigate$.next("");
    }

    if (!this.selectedLogon?.error && !this.isEdition) {
      this.store.dispatch(
        loadResetBlockUser({ selectedLogon: this.selectedLogon }),
      );
    }
  }
}
